import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { propertyVisitsAPI, propertiesAPI, clientsAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import {
  Eye, Plus, Search, Trash2, Download, AlertTriangle, CheckCircle2,
  Clock, Building2, Calendar, User, MapPin, ChevronDown, ChevronUp,
  Wrench, Zap, Paintbrush, Lock, DoorOpen, CloudRain, Hammer,
  Camera, Image as ImageIcon, X, Loader2
} from 'lucide-react';

const CATEGORY_ICONS = {
  plomberie: Wrench, electricite: Zap, peinture: Paintbrush,
  serrurerie: Lock, menuiserie: DoorOpen, humidite: CloudRain,
  toiture: Building2, fissure: AlertTriangle, autre: Hammer,
};

const CATEGORY_LABELS = {
  plomberie: "Plomberie", electricite: "Électricité", peinture: "Peinture",
  serrurerie: "Serrurerie", menuiserie: "Menuiserie", toiture: "Toiture",
  humidite: "Humidité", fissure: "Fissure", vitrerie: "Vitrerie",
  nettoyage: "Nettoyage", securite: "Sécurité",
  jardin_exterieur: "Jardin/Extérieur", autre: "Autre"
};

const STATUS_MAP = {
  planifiee: { label: 'Planifiée', color: 'bg-blue-100 text-blue-700' },
  en_cours: { label: 'En cours', color: 'bg-amber-100 text-amber-700' },
  terminee: { label: 'Terminée', color: 'bg-green-100 text-green-700' },
};

const PRIORITY_MAP = {
  faible: { label: 'Faible', color: 'bg-green-100 text-green-700' },
  normale: { label: 'Normale', color: 'bg-amber-100 text-amber-700' },
  urgente: { label: 'Urgente', color: 'bg-red-100 text-red-700' },
};

const ANOMALY_STATUS = {
  signale: { label: 'Signalé', color: 'bg-red-100 text-red-700' },
  en_cours: { label: 'En cours', color: 'bg-amber-100 text-amber-700' },
  resolu: { label: 'Résolu', color: 'bg-green-100 text-green-700' },
};

const PropertyVisits = () => {
  const [visits, setVisits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Create visit modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ property_id: '', visit_date: '', inspector_name: '', notes: '' });

  // Detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);

  // Add anomaly modal
  const [showAnomalyModal, setShowAnomalyModal] = useState(false);
  const [anomalyForm, setAnomalyForm] = useState({
    location_type: 'partie_commune', tenant_id: '', tenant_name: '', category: 'plomberie', description: '', priority: 'normale'
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [visitsRes, propsRes, tenantsRes] = await Promise.all([
        propertyVisitsAPI.getAll(),
        propertiesAPI.getAll({ transaction_type: 'location' }),
        clientsAPI.getAll({ client_type: 'locataire' })
      ]);
      setVisits(visitsRes.data);
      setProperties(propsRes.data);
      setTenants(tenantsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateVisit = async (e) => {
    e.preventDefault();
    if (!createForm.property_id || !createForm.visit_date || !createForm.inspector_name) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    try {
      await propertyVisitsAPI.create(createForm);
      toast.success('Visite créée avec succès');
      setShowCreateModal(false);
      setCreateForm({ property_id: '', visit_date: '', inspector_name: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleDeleteVisit = async (id) => {
    if (!window.confirm('Supprimer cette visite ?')) return;
    try {
      await propertyVisitsAPI.delete(id);
      toast.success('Visite supprimée');
      fetchData();
      if (selectedVisit?.id === id) setShowDetailModal(false);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUpdateStatus = async (visitId, newStatus) => {
    try {
      await propertyVisitsAPI.update(visitId, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchData();
      if (selectedVisit?.id === visitId) {
        const updated = await propertyVisitsAPI.getById(visitId);
        setSelectedVisit(updated.data);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleAddAnomaly = async (e) => {
    e.preventDefault();
    if (!anomalyForm.description || !anomalyForm.category) {
      toast.error('Veuillez remplir la description et la catégorie');
      return;
    }
    try {
      const payload = { ...anomalyForm };
      if (payload.location_type === 'locataire' && payload.tenant_id) {
        const t = tenants.find(t => t.id === payload.tenant_id);
        if (t) payload.tenant_name = t.name;
      }
      await propertyVisitsAPI.addAnomaly(selectedVisit.id, payload);
      toast.success('Anomalie ajoutée');
      setShowAnomalyModal(false);
      setAnomalyForm({ location_type: 'partie_commune', tenant_id: '', tenant_name: '', category: 'plomberie', description: '', priority: 'normale' });
      const updated = await propertyVisitsAPI.getById(selectedVisit.id);
      setSelectedVisit(updated.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleUpdateAnomalyStatus = async (visitId, anomalyId, newStatus) => {
    try {
      await propertyVisitsAPI.updateAnomaly(visitId, anomalyId, newStatus);
      toast.success(`Anomalie: ${ANOMALY_STATUS[newStatus]?.label}`);
      const updated = await propertyVisitsAPI.getById(visitId);
      setSelectedVisit(updated.data);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDownloadPdf = async (visitId) => {
    try {
      const response = await propertyVisitsAPI.downloadPdf(visitId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport_visite_${visitId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Rapport PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleUploadPhotos = async (visitId, anomalyId, files) => {
    if (!files || files.length === 0) return;
    setUploadingPhoto(anomalyId);
    try {
      const formData = new FormData();
      Array.from(files).forEach(f => formData.append('files', f));
      await propertyVisitsAPI.uploadAnomalyPhotos(visitId, anomalyId, formData);
      toast.success(`${files.length} photo(s) ajoutee(s)`);
      const updated = await propertyVisitsAPI.getById(visitId);
      setSelectedVisit(updated.data);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handleDeletePhoto = async (visitId, anomalyId, photoUrl) => {
    try {
      await propertyVisitsAPI.deleteAnomalyPhoto(visitId, anomalyId, photoUrl);
      toast.success('Photo supprimee');
      const updated = await propertyVisitsAPI.getById(visitId);
      setSelectedVisit(updated.data);
    } catch {
      toast.error('Erreur');
    }
  };

  const filteredVisits = visits.filter(v => {
    const matchSearch = !searchTerm || v.property_title?.toLowerCase().includes(searchTerm.toLowerCase()) || v.reference?.toLowerCase().includes(searchTerm.toLowerCase()) || v.inspector_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === 'all' || v.status === filterStatus;
    return matchSearch && matchStatus;
  });

  // Get tenants for a specific property
  const getPropertyTenants = (propertyId) => {
    return tenants.filter(t => t.assigned_property_id === propertyId);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="property-visits-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Visites de Biens</h1>
          <p className="text-gray-500 mt-1">Planifiez et suivez les visites et anomalies</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="create-visit-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle Visite
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-semibold text-gray-900">{visits.length}</p>
          <p className="text-xs text-gray-500">Total Visites</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-semibold text-blue-600">{visits.filter(v => v.status === 'planifiee').length}</p>
          <p className="text-xs text-gray-500">Planifiées</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-semibold text-amber-600">{visits.reduce((sum, v) => sum + (v.anomalies?.filter(a => a.status !== 'resolu').length || 0), 0)}</p>
          <p className="text-xs text-gray-500">Anomalies ouvertes</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-semibold text-red-600">{visits.reduce((sum, v) => sum + (v.anomalies?.filter(a => a.priority === 'urgente' && a.status !== 'resolu').length || 0), 0)}</p>
          <p className="text-xs text-gray-500">Urgentes</p>
        </CardContent></Card>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Rechercher..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-visits" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]" data-testid="filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="planifiee">Planifiée</SelectItem>
            <SelectItem value="en_cours">En cours</SelectItem>
            <SelectItem value="terminee">Terminée</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Visits List */}
      <div className="space-y-3">
        {filteredVisits.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-gray-500">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Aucune visite trouvée</p>
          </CardContent></Card>
        ) : filteredVisits.map(visit => {
          const anomCount = visit.anomalies?.length || 0;
          const openAnom = visit.anomalies?.filter(a => a.status !== 'resolu').length || 0;
          const urgentAnom = visit.anomalies?.filter(a => a.priority === 'urgente' && a.status !== 'resolu').length || 0;
          return (
            <Card key={visit.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => { setSelectedVisit(visit); setShowDetailModal(true); }} data-testid={`visit-card-${visit.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-gray-400">{visit.reference}</span>
                      <Badge className={STATUS_MAP[visit.status]?.color || 'bg-gray-100'}>{STATUS_MAP[visit.status]?.label}</Badge>
                    </div>
                    <p className="font-medium text-gray-900 truncate">{visit.property_title}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{visit.visit_date}</span>
                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{visit.inspector_name}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {anomCount > 0 && (
                      <div className="text-center">
                        <div className="flex items-center gap-1">
                          <AlertTriangle className={`w-4 h-4 ${urgentAnom > 0 ? 'text-red-500' : 'text-amber-500'}`} />
                          <span className="font-semibold text-gray-900">{openAnom}</span>
                          <span className="text-xs text-gray-400">/ {anomCount}</span>
                        </div>
                        <p className="text-xs text-gray-400">anomalies</p>
                      </div>
                    )}
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" onClick={() => handleDownloadPdf(visit.id)} title="Rapport PDF" data-testid={`pdf-visit-${visit.id}`}>
                        <Download className="w-4 h-4 text-[#1B3A4B]" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteVisit(visit.id)} title="Supprimer" data-testid={`delete-visit-${visit.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create Visit Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Nouvelle Visite</DialogTitle>
            <DialogDescription>Planifiez une visite de bien immobilier</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateVisit} className="space-y-4">
            <div><Label>Bien immobilier *</Label>
              <Select value={createForm.property_id} onValueChange={v => setCreateForm(f => ({ ...f, property_id: v }))}>
                <SelectTrigger data-testid="select-property"><SelectValue placeholder="Sélectionner un bien" /></SelectTrigger>
                <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.title} - {p.district}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date de visite *</Label>
              <Input type="date" value={createForm.visit_date} onChange={e => setCreateForm(f => ({ ...f, visit_date: e.target.value }))} data-testid="visit-date" />
            </div>
            <div><Label>Inspecteur *</Label>
              <Input value={createForm.inspector_name} onChange={e => setCreateForm(f => ({ ...f, inspector_name: e.target.value }))} placeholder="Nom de l'inspecteur" data-testid="inspector-name" />
            </div>
            <div><Label>Notes</Label>
              <Textarea value={createForm.notes} onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes de visite..." data-testid="visit-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="submit-visit">Créer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Visit Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto">
          {selectedVisit && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#1B3A4B]" />
                  {selectedVisit.property_title}
                </DialogTitle>
                <DialogDescription>{selectedVisit.reference} - {selectedVisit.visit_date}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Info & Status */}
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={STATUS_MAP[selectedVisit.status]?.color}>{STATUS_MAP[selectedVisit.status]?.label}</Badge>
                  <span className="text-sm text-gray-500">Inspecteur: {selectedVisit.inspector_name}</span>
                  <div className="ml-auto flex gap-2">
                    {selectedVisit.status === 'planifiee' && <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(selectedVisit.id, 'en_cours')} data-testid="start-visit-btn">Démarrer</Button>}
                    {selectedVisit.status === 'en_cours' && <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(selectedVisit.id, 'terminee')} className="text-green-600" data-testid="finish-visit-btn"><CheckCircle2 className="w-4 h-4 mr-1" />Terminer</Button>}
                  </div>
                </div>
                {selectedVisit.notes && <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{selectedVisit.notes}</p>}

                {/* Anomalies */}
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Anomalies ({selectedVisit.anomalies?.length || 0})</h3>
                  <Button size="sm" onClick={() => setShowAnomalyModal(true)} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="add-anomaly-btn">
                    <Plus className="w-4 h-4 mr-1" /> Signaler
                  </Button>
                </div>

                {(!selectedVisit.anomalies || selectedVisit.anomalies.length === 0) ? (
                  <p className="text-center text-gray-400 py-4 text-sm">Aucune anomalie signalée</p>
                ) : (
                  <div className="space-y-2">
                    {selectedVisit.anomalies.map(anomaly => {
                      const CatIcon = CATEGORY_ICONS[anomaly.category] || AlertTriangle;
                      return (
                        <div key={anomaly.id} className="border border-gray-100 rounded-lg p-3" data-testid={`anomaly-${anomaly.id}`}>
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${anomaly.priority === 'urgente' ? 'bg-red-100' : anomaly.priority === 'faible' ? 'bg-green-100' : 'bg-amber-100'}`}>
                              <CatIcon className={`w-4 h-4 ${anomaly.priority === 'urgente' ? 'text-red-600' : anomaly.priority === 'faible' ? 'text-green-600' : 'text-amber-600'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="font-medium text-sm">{CATEGORY_LABELS[anomaly.category] || anomaly.category}</span>
                                <Badge className={PRIORITY_MAP[anomaly.priority]?.color + ' text-xs'}>{PRIORITY_MAP[anomaly.priority]?.label}</Badge>
                                <Badge className={ANOMALY_STATUS[anomaly.status]?.color + ' text-xs'}>{ANOMALY_STATUS[anomaly.status]?.label}</Badge>
                                <Badge variant="outline" className="text-xs">
                                  {anomaly.location_type === 'partie_commune' ? 'Partie commune' : `Locataire: ${anomaly.tenant_name || ''}`}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600">{anomaly.description}</p>
                              <p className="text-xs text-gray-400 mt-1">Signale par {anomaly.reported_by} le {anomaly.reported_at?.split('T')[0]}</p>

                              {/* Photos gallery */}
                              {anomaly.photos && anomaly.photos.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {anomaly.photos.map((photo, pIdx) => (
                                    <div key={pIdx} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-gray-200 cursor-pointer"
                                      onClick={() => setPhotoPreview(photo)}>
                                      <img src={`${process.env.REACT_APP_BACKEND_URL}${photo}`} alt={`Anomalie photo ${pIdx + 1}`}
                                        className="w-full h-full object-cover" />
                                      <button onClick={(e) => { e.stopPropagation(); handleDeletePhoto(selectedVisit.id, anomaly.id, photo); }}
                                        className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                        data-testid={`delete-photo-${pIdx}`}>
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Upload button */}
                              <div className="mt-2">
                                <label className="inline-flex items-center gap-1.5 text-xs text-[#1B3A4B] cursor-pointer hover:underline"
                                  data-testid={`upload-photo-${anomaly.id}`}>
                                  {uploadingPhoto === anomaly.id
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <Camera className="w-3.5 h-3.5" />}
                                  {uploadingPhoto === anomaly.id ? 'Upload...' : 'Ajouter photos'}
                                  <input type="file" multiple accept="image/*" className="hidden"
                                    onChange={e => handleUploadPhotos(selectedVisit.id, anomaly.id, e.target.files)}
                                    disabled={uploadingPhoto === anomaly.id} />
                                </label>
                              </div>
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              {anomaly.status === 'signale' && (
                                <Button size="sm" variant="outline" className="text-xs" onClick={() => handleUpdateAnomalyStatus(selectedVisit.id, anomaly.id, 'en_cours')} data-testid={`anomaly-progress-${anomaly.id}`}>
                                  <Clock className="w-3 h-3 mr-1" /> En cours
                                </Button>
                              )}
                              {(anomaly.status === 'signale' || anomaly.status === 'en_cours') && (
                                <Button size="sm" variant="outline" className="text-xs text-green-600" onClick={() => handleUpdateAnomalyStatus(selectedVisit.id, anomaly.id, 'resolu')} data-testid={`anomaly-resolve-${anomaly.id}`}>
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Résolu
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => handleDownloadPdf(selectedVisit.id)} data-testid="detail-pdf-btn">
                  <Download className="w-4 h-4 mr-2" /> Rapport PDF
                </Button>
                <Button onClick={() => setShowDetailModal(false)}>Fermer</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Anomaly Modal */}
      <Dialog open={showAnomalyModal} onOpenChange={setShowAnomalyModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Signaler une Anomalie
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddAnomaly} className="space-y-4">
            <div><Label>Localisation *</Label>
              <Select value={anomalyForm.location_type} onValueChange={v => setAnomalyForm(f => ({ ...f, location_type: v, tenant_id: '', tenant_name: '' }))}>
                <SelectTrigger data-testid="anomaly-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="partie_commune">Partie commune</SelectItem>
                  <SelectItem value="locataire">Chez un locataire</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {anomalyForm.location_type === 'locataire' && selectedVisit && (
              <div><Label>Locataire *</Label>
                <Select value={anomalyForm.tenant_id} onValueChange={v => setAnomalyForm(f => ({ ...f, tenant_id: v }))}>
                  <SelectTrigger data-testid="anomaly-tenant"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    {getPropertyTenants(selectedVisit.property_id).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    {getPropertyTenants(selectedVisit.property_id).length === 0 && <SelectItem value="__none" disabled>Aucun locataire</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Catégorie *</Label>
              <Select value={anomalyForm.category} onValueChange={v => setAnomalyForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="anomaly-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Priorité</Label>
              <Select value={anomalyForm.priority} onValueChange={v => setAnomalyForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="anomaly-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="faible">Faible</SelectItem>
                  <SelectItem value="normale">Normale</SelectItem>
                  <SelectItem value="urgente">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Description *</Label>
              <Textarea value={anomalyForm.description} onChange={e => setAnomalyForm(f => ({ ...f, description: e.target.value }))} placeholder="Décrivez l'anomalie en détail..." rows={3} data-testid="anomaly-description" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAnomalyModal(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="submit-anomaly">Signaler</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Photo Preview Modal */}
      {photoPreview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPhotoPreview(null)}>
          <div className="relative max-w-4xl max-h-[90vh]">
            <button onClick={() => setPhotoPreview(null)} className="absolute -top-3 -right-3 bg-white rounded-full p-1 shadow-lg z-10">
              <X className="w-5 h-5" />
            </button>
            <img src={`${process.env.REACT_APP_BACKEND_URL}${photoPreview}`} alt="Anomalie"
              className="max-w-full max-h-[85vh] object-contain rounded-lg" onClick={e => e.stopPropagation()} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyVisits;
